import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { UUID } from '@/types';
import { completed, loading } from '@/actions/helpers';
import {
  DASHBOARD_CONTENT_LOADED,
  DASHBOARD_FILTERS_LOADED,
  DASHBOARD_LOADED,
  DASHBOARD_REFRESHED,
  EMBEDDED_USER_NOT_LOGGED_IN,
} from '@/actions/constants';
import DashboardRef from '@/util/DashboardRef';
import DashboardFilterRef from '@/util/DashboardFilterRef';
import LayerWhere from '@/components/layers/LayerWhere';
import ViewRef from '@/util/ViewRef';
import { ColumnType } from '@/util/ColumnType';
import { getDashboardRefreshId } from '@/store-selectors';
import { getJwtToken } from '@/features/customer-facing-dashboards/embeddedDashboardAuthUtils';

export const GET_DASHBOARD_INFO = 'getDashboardInfo';
export const GET_WIDGETS_DATA = 'getWidgetsData';
export const GET_DASHBOARD_FILTERS = 'getDashboardFilters';
export const APPLY_FILTERS = 'applyFilters';
export const DOWNLOAD_CSV = 'downloadCsv';

export type EmbeddedDashboardFiltersResponse = {
  filters: (Omit<DashboardFilterRef, '_columnType'> & {
    _columnType: ColumnType;
  })[];
  filterValues: LayerWhere[];
};
export type EmbeddedDashboardFiltersRequest = {
  dashboardId: UUID;
  refreshId: Nullable<string>;
};

export type EmbeddedDashboardDataResponse = {
  _views: ViewRef[];
  _widgetContents: unknown[];
};

export type ApplyFiltersRequest = {
  dashboardId: UUID;
  appliedFilters: LayerWhere[];
};

export type ApplyFiltersResponse = {
  _refreshId: UUID;
};

export type DownloadCsvRequest = {
  dashboardId: UUID;
  seriesId?: Nullable<UUID>;
  refreshId: Nullable<string>;
};

const baseQuery = fetchBaseQuery({
  baseUrl: '/api/v1/embedded/dashboard',
  credentials: 'same-origin',
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json;charset=UTF-8');
    headers.set('x-requested-with', 'XMLHttpRequest');

    const token = getJwtToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    return headers;
  },
});

const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    // show modal that the token is expired
    api.dispatch(completed(EMBEDDED_USER_NOT_LOGGED_IN));
  }
  return result;
};

export const customerFacingDashboardApi = createApi({
  reducerPath: 'customerFacingDashboardApi',
  baseQuery: baseQueryWithReAuth,
  endpoints: (builder) => ({
    [DOWNLOAD_CSV]: builder.query<Blob, DownloadCsvRequest>({
      query: ({ dashboardId, seriesId, refreshId }) => {
        return {
          url: `/${dashboardId}/csv/${seriesId}/${refreshId}`,
          responseHandler: (response) => {
            return response.blob();
          },
        };
      },
    }),
    [GET_DASHBOARD_INFO]: builder.query({
      query: (dashboardId?: UUID) => `/${dashboardId}/data`,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        dispatch(loading(DASHBOARD_LOADED, {}));
        const { data } = await queryFulfilled;
        const dashboardRef = new DashboardRef(
          null,
          data._latestRefreshId,
          data._name,
          null,
        );
        dispatch(
          completed(DASHBOARD_LOADED, { json: data, dashboard: dashboardRef }),
        );
      },
    }),

    [GET_DASHBOARD_FILTERS]: builder.query<
      EmbeddedDashboardFiltersResponse,
      EmbeddedDashboardFiltersRequest
    >({
      query: ({ dashboardId, refreshId }) =>
        `/${dashboardId}/filters?refreshId=${refreshId}`,
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        dispatch(loading(DASHBOARD_FILTERS_LOADED));
        const { data } = await queryFulfilled;
        const filters = data.filters.map((f) =>
          DashboardFilterRef.buildFromServer(f),
        );
        const appliedFilters = data.filterValues.map((v) =>
          LayerWhere.buildFromServer(v),
        );
        dispatch(
          completed(DASHBOARD_FILTERS_LOADED, {
            filters,
            appliedFilters,
          }),
        );
      },
    }),

    [GET_WIDGETS_DATA]: builder.query<
      EmbeddedDashboardDataResponse,
      {
        refreshId: Nullable<string>;
        dashboardId: UUID;
        viewsAwaitingResults: ViewRef[];
      }
    >({
      query: ({ dashboardId, refreshId, viewsAwaitingResults }) =>
        `${dashboardId}/views/data?refreshId=${refreshId}&viewSeries=${viewsAwaitingResults
          .map((it) => it._series)
          .join(',')}`,
      async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
        if (args.viewsAwaitingResults.length === 0) {
          return; // nothing to load
        }

        const currentRefresh = args.refreshId;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const dashboardRefresh = getDashboardRefreshId(getState());

        dispatch(
          loading(DASHBOARD_CONTENT_LOADED, {
            views: args.viewsAwaitingResults,
            showLoaderImmediately: true,
          }),
        );
        const { data } = await queryFulfilled;
        if (dashboardRefresh !== currentRefresh) {
          return;
        }
        dispatch(
          completed(DASHBOARD_CONTENT_LOADED, {
            json: data,
            incrementRetryTime: false,
          }),
        );
      },
    }),

    [APPLY_FILTERS]: builder.mutation<
      ApplyFiltersResponse,
      ApplyFiltersRequest
    >({
      query(request) {
        return {
          url: `/${request.dashboardId}/refresh`,
          method: 'POST',
          body: {
            force: true,
            filters: request.appliedFilters,
          },
        };
      },
      async onQueryStarted(args, { dispatch, getState, queryFulfilled }) {
        dispatch(loading(DASHBOARD_REFRESHED));
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { active, views, appliedFilters } = getState().dashboards;
        const { data } = await queryFulfilled;
        dispatch(
          completed(DASHBOARD_REFRESHED, {
            json: data,
            dashboard: active,
            views,
          }),
        );

        dispatch({
          type: 'DASHBOARD_REFRESHED_LEGACY',
          payload: {
            dashboardId: active._id,
            refreshId: data._refreshId,
            hasAppliedFilters: appliedFilters.length > 0,
          },
        });
      },
    }),
  }),
});

export const {
  useGetDashboardInfoQuery,
  useGetWidgetsDataQuery,
  useGetDashboardFiltersQuery,
  useApplyFiltersMutation,
  useLazyDownloadCsvQuery,
} = customerFacingDashboardApi;
